import { useEffect } from 'react'
import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from '../../components/ConfirmationModal'
import DebugAccordion from '../../components/DebugAccordion'
import JsonForm from '../../components/JsonForm.jsx'
import Loadable from '../../components/Loadable.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import ProfileNav from './components/ProfileNav'
import ProfileTools from './components/ProfileTools.jsx'

import { addNotification, NotificationTypes } from '../../state/actions/notificationActions.js'
import { fetchProfile, updateProfile } from '../../state/actions/profileActions'
import { fetchProfileDeviceSchema } from '../../state/actions/profileDeviceSchemaActions'
import { fetchProfileSchema } from '../../state/actions/profileSchemaActions'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'

import { profileSignal, profileCcSchemaSignal, profileLoadedSignal, profileErrorSignal } from '../../state/signals/profileSignals'
import { profileDeviceSchemaSignal, profileDeviceSchemaLoadedSignal } from '../../state/signals/profileDeviceSchemaSignals'
import { profileSchemaLoadedSignal, profileSchemaVersionSignal, profileSchemaErrorSignal } from '../../state/signals/profileSchemaSignals'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals'

import { checkDeviceDescriptionConflicts, isEmptyObject, makeFieldsReadonly } from '../../utils/utils'
import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

export default function ProfileDeviceDetail() {

    const navigate = useNavigate();
    const { beId, profileId, name } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/profiles`)
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const desc = searchParams.get('desc')
    const editMode = !!desc
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewProfiles) {
            addNotification('You are not authorized to view UTG profiles for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditProfiles
    })

    useEffect(() => {
        if (beIdChanged) return
        // Reset immediately so stale schema from a prior device visit doesn't flash
        // before the correct device schema loads
        profileDeviceSchemaLoadedSignal.value = false
        fetchSchemaManifest().then(
            () => {
                fetchProfileDeviceSchema(name, schemaManifestSignal.value, config, accessToken)
                fetchProfile(beId, profileId, config, accessToken).then(
                    () => {
                        fetchProfileSchema(beId, profileCcSchemaSignal.value, schemaManifestSignal.value, config, accessToken)
                    }
                )
            }
        )
    }, [beId, beIdChanged, profileId, name, config, accessToken])

    const deviceComputed = useComputed(() => {
        if (profileSignal.value && editMode) {
            const deviceList = profileSignal.value?.config?.deviceConfig?.device ?? []
            if (deviceList.length == 0) return {}

            const index = profileSignal.value.config.deviceConfig.device.findIndex(a => a.deviceType == name && a.taskDesc == desc)
            const currentDevice = profileSignal.value.config.deviceConfig.device[index]
            const jsonFormDevice = { config: currentDevice }
            return jsonFormDevice
        }
        return null
    })
    const deviceIndexComputed = useComputed(() => {
        if (profileSignal.value && editMode) {
            const deviceList = profileSignal.value?.config?.deviceConfig?.device ?? []
            if (deviceList.length == 0) return -1

            const index = profileSignal.value.config.deviceConfig.device.findIndex(a => a.deviceType == name && a.taskDesc == desc)
            return index
        }
        return -1
    })


    const pageTitleComputed = useComputed(() => {
        return editMode ?
            `Manage ${name} UTG Device` :
            `Add ${name} UTG Device`
    })

    const jsonFormDataComputed = useComputed(() => {

        if (!profileDeviceSchemaLoadedSignal.value || !profileLoadedSignal.value) {
            return
        }

        if (isEmptyObject(profileDeviceSchemaSignal.value) || isEmptyObject(profileSignal.value) || profileSignal.value == null) {
            return
        }

        // deep clone the schema
        let clonedSchema = structuredClone(profileDeviceSchemaSignal.value)

        // disable editing of all fields if canEditComputed is false
        if (!canEditComputed.value) {
            clonedSchema = makeFieldsReadonly(clonedSchema)
        }

        // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
        globalThis.SCHEMA_MODE = 'PROFILE'
        //console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)

        // NOTE: if onSubmitValid is changed to onSubmit then the callback must also be changed to receive both errors and values
        const formData = {
            schema: clonedSchema.properties.config,
            loadedComponentConfig: profileSignal.value.config,
            onSubmitValid: handleFormSubmit,
            onCancel: handleCancel,
            onFormChange: handleFormChange,
            canEditConfig: canEditComputed.value
        }
        if (deviceComputed.value) {
            formData.value = deviceComputed.value.config
        }
        console.log('formData from profile device config', formData)
        return formData
    })

    const showCancelModalSignal = useSignal(false)
    const isFormEditedSignal = useSignal(false)
    const isFormSubmittingSignal = useSignal(false)
    const accordionData = [
        { name: 'Device', data: deviceComputed.value },
        { name: 'Device Index', data: deviceIndexComputed.value },
        { name: 'Device Schema', data: profileDeviceSchemaSignal.value },
        { name: 'Component Profile', data: profileSignal.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config },
        { name: 'Schema Manifest', data: schemaManifestSignal.value }
    ]

    const handleFormSubmit = values => {

        //console.log('values', values)

        // short-circuit if the handler was already called. This blocks clicking the submit a second time while already processing a previous submit
        if (isFormSubmittingSignal.value) {
            return false
        }
        isFormSubmittingSignal.value = true

        try {
            //console.log('values', values)
            if (values) {

                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    id: profileSignal.value.id,
                    name: profileSignal.value.name,
                    businessEntityId: profileSignal.value.businessEntityId,
                    config: profileSignal.value.config,
                }

                if (editMode) {
                    // replace the existing selected apiInterface with data from values 
                    data.config.deviceConfig.device[deviceIndexComputed.value] = values
                }
                else {
                    // insert values into the apiInterface array
                    data.config.deviceConfig.device.push(values)
                }

                // validate taskDesc uniqueness in case jsonform validation fails
                if (checkDeviceDescriptionConflicts(data.config.deviceConfig.device)) {
                    addNotification("Unable to save due to conflict. All Devices must have a unique Device Name.", NotificationTypes.ERROR)
                    return false
                }

                // add a schema value to the config data. This allows us to load this specific schema rather than a default one in future edits
                data.config.utgGlobal.ccschema = profileSchemaVersionSignal.value

                //console.log(data)
                updateProfile(beId, profileId, data, config, accessToken, true)
                    .then(() => {
                        addNotification('Devices was successfully updated.', NotificationTypes.SUCCESS, true)
                    })
                    .catch(error => {
                        console.log(error.toString())
                        addNotification("Unable to update the Device.")
                        return false
                    })
                    .finally(() => {
                        isFormSubmittingSignal.value = false
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEditedSignal.value) {
            showCancelModalSignal.value = true
        }
        else {
            navigate('../')
        }
    }

    const handleFormChange = () => {
        //console.log('handleFormChange')
        isFormEditedSignal.value = true
    }

    return (
        <>
            <ConfirmationModal
                show={showCancelModalSignal.value}
                handleCancel={
                    () => showCancelModalSignal.value = false
                }
                handleConfirm={
                    () => navigate('../devices')
                }
                message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'}
                title={"Warning: Unsaved Changes"}
                cancelText="Continue Editing"
                confirmText="Lose Changes"
            />
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/profiles` }}>
                    Profile List
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{profileLoadedSignal.value ? <>Manage {profileSignal.value.name}</> : <>Loading...</>
                }</Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title={pageTitleComputed.value} canEditSignal={canEditComputed} />
                <Loadable loadedSignals={[profileLoadedSignal, profileSchemaLoadedSignal]} errorSignals={[profileErrorSignal, profileSchemaErrorSignal]}>
                    <ProfileTools profile={profileSignal.value} ></ProfileTools>
                </Loadable>
            </Stack>
            <ProfileNav activeKey="3"></ProfileNav>
            <Loadable loadedSignals={[profileLoadedSignal, profileSchemaLoadedSignal]} errorSignals={[profileErrorSignal, profileSchemaErrorSignal]}>
                <JsonForm data={jsonFormDataComputed.value}></JsonForm>
            </Loadable>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}